import React, { useEffect, useMemo, useState } from "react";
import {
  fetchByStaff,
  fetchByTable,
  fetchDaily,
  fetchPatterns,
  fetchOrderMix,
  fetchHourly,
  fetchLowPerformers,
  fetchRecipePlan,
  fetchSummary,
  fetchCancellations,
  fetchGroupEvents,
  fetchRevenueChannels,
  fetchServiceSpeed,
  fetchStationSpeed,
  fetchTopItems,
  getCurrency,
  hasFeature,
  resolveImageUrl,
  fetchFrontDeskSnapshot,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import FrontDeskFacts from "../components/FrontDeskFacts";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    failed_load: "Failed to load reports.",
    win_today: "Today",
    win_yesterday: "Yesterday",
    win_3d: "Last 3 days",
    win_week: "Last 7 days",
    win_month: "Last 30 days",
    custom_from: "From",
    custom_to: "To",
    custom_clear: "Clear",
    day_sub_custom: "Total orders per day across the selected range.",
    by_waiter: "Sales by waiter",
    by_waiter_sub: "Revenue and orders per waiter for the selected range — spot the outlier. High cancellations can signal a problem.",
    th_waiter: "Waiter",
    th_role: "Role",
    th_items: "Items",
    th_avg_check: "Avg. check",
    th_cancelled: "Cancelled",
    no_waiter_data: "No waiter-attributed sales for this range yet.",
    no_data_window: "No data yet for this window.",
    no_data: "No data.",
    page_title: "Manager Reports",
    ev_title: "🎉 Group & event orders", ev_none: "No group or event orders booked in this range yet.", ev_events: "Events", ev_guests: "Guests", ev_revenue: "Revenue", ev_per_guest: "Per guest", ev_upcoming: "Upcoming", ev_cancelled: "{n} cancelled", ev_top: "Top events", ev_buffet: "Buffet", ev_alacarte: "À la carte", ev_pax: "pax",
    ch_title: "💳 Revenue by channel", ch_none: "No revenue in this range yet.", ch_qr: "QR self-order", ch_captain: "Captain", ch_room: "Room service", ch_group: "Group & event", ch_orders_n: "{n} orders",
    cx_title: "🚫 Cancellations", cx_none: "No cancellations in this range — clean run.", cx_cancelled: "Cancelled", cx_rate: "Rate", cx_lost: "Revenue lost", cx_top: "Most-cancelled items", cx_qty: "{n}×",
    loading: "Loading…",
    kpi_revenue: "Revenue",
    kpi_items: "{n} items",
    kpi_orders: "Orders",
    kpi_active: "{n} active",
    kpi_completed: "Completed",
    kpi_cancelled: "Cancelled",
    kpi_avg_order: "Avg. order",
    svc_title: "\u23f1\ufe0f Service speed", svc_cook: "Cook time", svc_wait: "Total wait", svc_min: "min", svc_median: "median", svc_avg: "avg", svc_orders: "orders", svc_none: "No timing data in this range yet \u2014 it builds up as staff move tickets through the kitchen.",
    most_ordered: "🔥 Most ordered items",
    th_rating: "Rating",
    most_ordered_sub: "Use this to plan recipes & stock.",
    orders_by_hour: "🕓 Orders by hour",
    peak_hour: "Peak hour:",
    peak_hour_orders: "({n} orders)",
    orders_by_day: "📅 Orders by day of week",
    busy_title: "💰 Revenue by time", by_daypart: "By daypart", by_dow: "By day of week", dp_breakfast: "Breakfast", dp_lunch: "Lunch", dp_dinner: "Dinner", dp_late: "Late", bd1: "Mon", bd2: "Tue", bd3: "Wed", bd4: "Thu", bd5: "Fri", bd6: "Sat", bd7: "Sun",
    grp_sales: "Sales & menu", grp_busy: "Busy times", grp_team: "Team", grp_kitchen: "Kitchen", grp_ordering: "Ordering", how_order: "How guests order", ch_self: "Guest self-order (QR)", ch_waiter: "Captain order", aov_short: "avg", rounds_title: "Rounds per tab", rounds_per_tab: "rounds / tab", tabs_word: "tabs", round_dist: "Tabs by rounds", rev_by_round: "Revenue by round", round_n: "Round {n}", no_rounds: "No open-tab rounds in this range.", aov_lead: "Higher average ticket: {w}", aov_even: "Both channels spend about the same",
    day_sub_today: "Today's orders.",
    day_sub_month: "Total orders per day across the last 30 days.",
    day_sub_week: "Total orders per day across the last 7 days.",
    revenue_by_table: "💺 Revenue by table",
    no_table_orders: "No completed table orders yet.",
    th_table: "Table", th_area: "Area", area_unassigned: "No area",
    th_orders: "Orders",
    th_revenue: "Revenue",
    low_performers: "🪫 Low performers",
    low_performers_sub: "Consider removing, re-pricing, or promoting these.",
    not_enough_data: "Not enough data yet.",
    th_item: "Item",
    th_category: "Category",
    th_sold: "Sold",
    recipe_plan: "📒 Recipe plan",
    recipe_plan_sub: "Top sellers in the {n} with their stored recipe and a forecast for the next equivalent period.",
    recipe_plan_window: "window",
    not_enough_orders: "Not enough orders yet to plan from.",
    th_per_day: "Per day",
    th_forecast: "Forecast",
    th_recipe: "Recipe",
    no_recipe: "No recipe yet — add one in Menu.",
  },
  am: {
    failed_load: "ሪፖርቶችን መጫን አልተሳካም።",
    win_today: "ዛሬ",
    win_week: "ያለፉት 7 ቀናት",
    win_month: "ያለፉት 30 ቀናት",
    no_data_window: "ለዚህ ጊዜ እስካሁን ምንም መረጃ የለም።",
    no_data: "ምንም መረጃ የለም።",
    page_title: "የአስተዳዳሪ ሪፖርቶች",
    ev_title: "🎉 የቡድንና ዝግጅት ትዕዛዞች", ev_none: "በዚህ ጊዜ ውስጥ የተያዘ የቡድን ወይም ዝግጅት ትዕዛዝ የለም።", ev_events: "ዝግጅቶች", ev_guests: "እንግዶች", ev_revenue: "ገቢ", ev_per_guest: "በእንግዳ", ev_upcoming: "መጪ", ev_cancelled: "{n} ተሰርዟል", ev_top: "ከፍተኛ ዝግጅቶች", ev_buffet: "ቡፌ", ev_alacarte: "በነጠላ", ev_pax: "ሰው",
    ch_title: "💳 በቻናል ገቢ", ch_none: "በዚህ ጊዜ ውስጥ ገቢ የለም።", ch_qr: "በQR ራስ-ትዕዛዝ", ch_captain: "በአስተናጋጅ", ch_room: "የክፍል አገልግሎት", ch_group: "ቡድንና ዝግጅት", ch_orders_n: "{n} ትዕዛዞች",
    cx_title: "🚫 የተሰረዙ ትዕዛዞች", cx_none: "በዚህ ጊዜ ውስጥ የተሰረዘ የለም — ንጹህ አፈጻጸም።", cx_cancelled: "ተሰርዟል", cx_rate: "መጠን", cx_lost: "የጠፋ ገቢ", cx_top: "በጣም የተሰረዙ ምግቦች", cx_qty: "{n}×",
    loading: "በመጫን ላይ…",
    kpi_revenue: "ገቢ",
    kpi_items: "{n} ዕቃዎች",
    kpi_orders: "ትዕዛዞች",
    kpi_active: "{n} ንቁ",
    kpi_completed: "የተጠናቀቁ",
    kpi_cancelled: "የተሰረዙ",
    kpi_avg_order: "አማካይ ትዕዛዝ",
    svc_title: "\u23f1\ufe0f የአገልግሎት ፍጥነት", svc_cook: "የማብሰያ ጊዜ", svc_wait: "ጠቅላላ ቆይታ", svc_min: "ደቂቃ", svc_median: "መካከለኛ", svc_avg: "አማካይ", svc_orders: "ትዕዛዞች", svc_none: "በዚህ ጊዜ ውስጥ የጊዜ መረጃ የለም \u2014 ሠራተኞች ትዕዛዞችን ሲያንቀሳቅሱ ይከማቻል።",
    most_ordered: "🔥 በብዛት የታዘዙ ዕቃዎች",
    th_rating: "ደረጃ",
    most_ordered_sub: "ይህንን የምግብ አዘገጃጀትና ክምችት ለማቀድ ይጠቀሙ።",
    orders_by_hour: "🕓 ትዕዛዞች በሰዓት",
    peak_hour: "የጫፍ ሰዓት:",
    peak_hour_orders: "({n} ትዕዛዞች)",
    orders_by_day: "📅 ትዕዛዞች በሳምንቱ ቀን",
    busy_title: "💰 ገቢ በጊዜ", by_daypart: "በቀኑ ክፍል", by_dow: "በሳምንቱ ቀን", dp_breakfast: "ቁርስ", dp_lunch: "ምሳ", dp_dinner: "እራት", dp_late: "ሌሊት", bd1: "ሰኞ", bd2: "ማክሰ", bd3: "ረቡዕ", bd4: "ሐሙስ", bd5: "ዓርብ", bd6: "ቅዳሜ", bd7: "እሁድ",
    grp_sales: "ሽያጭ እና ምናሌ", grp_busy: "የሥራ ሰዓቶች", grp_team: "ቡድን", grp_kitchen: "ኩሽና", grp_ordering: "የማዘዣ መንገድ", how_order: "እንግዶች እንዴት ያዛሉ", ch_self: "በራስ ማዘዝ (QR)", ch_waiter: "በአስተናጋጅ", aov_short: "አማካይ", rounds_title: "በክፍት ሂሳብ ዙሮች", rounds_per_tab: "ዙሮች / ሂሳብ", tabs_word: "ሂሳቦች", round_dist: "ሂሳቦች በዙር", rev_by_round: "ገቢ በዙር", round_n: "ዙር {n}", no_rounds: "በዚህ ክልል ክፍት ሂሳብ ዙሮች የሉም።", aov_lead: "ከፍ ያለ አማካይ ትዕዛዝ: {w}", aov_even: "ሁለቱም መንገዶች ተመሳሳይ ያወጣሉ",
    day_sub_today: "የዛሬ ትዕዛዞች።",
    day_sub_month: "ባለፉት 30 ቀናት ውስጥ በቀን ጠቅላላ ትዕዛዞች።",
    day_sub_week: "ባለፉት 7 ቀናት ውስጥ በቀን ጠቅላላ ትዕዛዞች።",
    revenue_by_table: "💺 ገቢ በጠረጴዛ",
    no_table_orders: "እስካሁን የተጠናቀቁ የጠረጴዛ ትዕዛዞች የሉም።",
    th_table: "ጠረጴዛ", th_area: "አካባቢ", area_unassigned: "ምንም አካባቢ",
    th_orders: "ትዕዛዞች",
    th_revenue: "ገቢ",
    low_performers: "🪫 ዝቅተኛ ሽያጭ ያላቸው",
    low_performers_sub: "እነዚህን ማስወገድ፣ ዋጋ ማስተካከል ወይም ማስተዋወቅ ያስቡ።",
    not_enough_data: "እስካሁን በቂ መረጃ የለም።",
    th_item: "ዕቃ",
    th_category: "ምድብ",
    th_sold: "የተሸጠ",
    recipe_plan: "📒 የምግብ አዘገጃጀት እቅድ",
    recipe_plan_sub: "በ{n} ውስጥ ቀዳሚ ሽያጮች ከተከማቸ አዘገጃጀታቸውና ለሚቀጥለው ተመጣጣኝ ጊዜ ትንበያ ጋር።",
    recipe_plan_window: "ጊዜ",
    not_enough_orders: "ለማቀድ እስካሁን በቂ ትዕዛዞች የሉም።",
    th_per_day: "በቀን",
    th_forecast: "ትንበያ",
    th_recipe: "አዘገጃጀት",
    no_recipe: "እስካሁን አዘገጃጀት የለም — በምናሌ ውስጥ ይጨምሩ።",
  },
  om: {
    failed_load: "Gabaasaalee fe'uu hin dandeenye.",
    win_today: "Har'a",
    win_week: "Guyyoota 7 darban",
    win_month: "Guyyoota 30 darban",
    no_data_window: "Yeroo kanaaf ammaaf daataan hin jiru.",
    no_data: "Daataan hin jiru.",
    page_title: "Gabaasaalee Bulchaa",
    ev_title: "🎉 Ajaja garee fi sagantaa", ev_none: "Yeroo kana keessatti ajajni garee yookaan sagantaa hin galmoofne.", ev_events: "Sagantaalee", ev_guests: "Keessummoota", ev_revenue: "Galii", ev_per_guest: "Nama tokkoon", ev_upcoming: "Dhufaa jiru", ev_cancelled: "{n} haqame", ev_top: "Sagantaalee ol'aanaa", ev_buffet: "Buffet", ev_alacarte: "Tokko tokkoon", ev_pax: "nama",
    ch_title: "💳 Galii karaa tajaajilaatiin", ch_none: "Yeroo kana keessatti galiin hin jiru.", ch_qr: "QR ofii ajajuu", ch_captain: "Kaapheen", ch_room: "Tajaajila kutaa", ch_group: "Garee fi sagantaa", ch_orders_n: "ajajoota {n}",
    cx_title: "🚫 Haquu ajajaa", cx_none: "Yeroo kana keessatti wanti haqame hin jiru — adeemsa qulqulluu.", cx_cancelled: "Haqame", cx_rate: "Reeshoo", cx_lost: "Galii bade", cx_top: "Nyaatota baay'ee haqaman", cx_qty: "{n}×",
    loading: "Fe'aat jira…",
    kpi_revenue: "Galii",
    kpi_items: "wantoota {n}",
    kpi_orders: "Ajajoota",
    kpi_active: "{n} kan socho'aa",
    kpi_completed: "Kan xumurame",
    kpi_cancelled: "Kan haqame",
    kpi_avg_order: "Ajaja giddu-galeessa",
    svc_title: "\u23f1\ufe0f Saffisa tajaajilaa", svc_cook: "Yeroo bilcheessuu", svc_wait: "Eegumsa guutuu", svc_min: "daq.", svc_median: "giddu", svc_avg: "gidd.", svc_orders: "ajajoota", svc_none: "Yeroo kana keessatti daataan yeroo hin jiru \u2014 hojjettoonni ajaja sochoosaa yoo deeman walitti qabama.",
    most_ordered: "🔥 Wantoota baay'inaan ajajaman",
    th_rating: "Madaallii",
    most_ordered_sub: "Kana qophii nyaataa fi kuusaa karoorsuuf fayyadami.",
    orders_by_hour: "🕓 Ajajoota sa'aatiidhaan",
    peak_hour: "Sa'aatii fe'umsaa:",
    peak_hour_orders: "(ajajoota {n})",
    orders_by_day: "📅 Ajajoota guyyaa torbaniitiin",
    busy_title: "💰 Galii yeroodhaan", by_daypart: "Yeroo guyyaatiin", by_dow: "Guyyaa torbaniitiin", dp_breakfast: "Ciree", dp_lunch: "Laaqana", dp_dinner: "Irbaata", dp_late: "Halkan", bd1: "Wix", bd2: "Kib", bd3: "Rob", bd4: "Kam", bd5: "Jim", bd6: "San", bd7: "Dil",
    grp_sales: "Gurgurtaa fi menuu", grp_busy: "Yeroo hojii", grp_team: "Garee", grp_kitchen: "Kushiinaa", grp_ordering: "Karaa ajajaa", how_order: "Keessummoonni akkamitti ajaju", ch_self: "Ofiin ajajuu (QR)", ch_waiter: "Tajaajilaadhaan", aov_short: "gidd.", rounds_title: "Marsaa taabii tokkoon", rounds_per_tab: "marsaa / taabii", tabs_word: "taabiiwwan", round_dist: "Taabii marsaadhaan", rev_by_round: "Galii marsaadhaan", round_n: "Marsaa {n}", no_rounds: "Yeroo kana keessa marsaan taabii hin jiru.", aov_lead: "Gatii ajaja giddugaleessaa olaanaa: {w}", aov_even: "Karaaleen lamaanuu wal-qixa baasu",
    day_sub_today: "Ajajoota har'aa.",
    day_sub_month: "Guyyoota 30 darban keessatti ajajoota guyyaatti waliigalaa.",
    day_sub_week: "Guyyoota 7 darban keessatti ajajoota guyyaatti waliigalaa.",
    revenue_by_table: "💺 Galii minjaalaan",
    no_table_orders: "Ammaaf ajajoota minjaalaa xumuraman hin jiran.",
    th_table: "Minjaala", th_area: "Naannoo", area_unassigned: "Naannoo hin jiru",
    th_orders: "Ajajoota",
    th_revenue: "Galii",
    low_performers: "🪫 Gurgurtaa gadi aanaa",
    low_performers_sub: "Kanneen haquu, gatii jijjiiruu, ykn beeksisuu yaadi.",
    not_enough_data: "Ammaaf daataan ga'aan hin jiru.",
    th_item: "Wanta",
    th_category: "Ramaddii",
    th_sold: "Kan gurguramae",
    recipe_plan: "📒 Karoora qophii nyaataa",
    recipe_plan_sub: "{n} keessatti gurgurtaa olaanaa qophii isaanii kuufame fi tilmaama yeroo itti aanu wajjin.",
    recipe_plan_window: "yeroo",
    not_enough_orders: "Ammaaf karoorsuuf ajajoonni ga'aan hin jiran.",
    th_per_day: "Guyyaatti",
    th_forecast: "Tilmaama",
    th_recipe: "Qophii",
    no_recipe: "Ammaaf qophiin hin jiru — Menu keessatti dabali.",
  },
};

// 402 / detail-object errors from the API come back as an object, not a
// string. Render them safely instead of letting React choke on it.
function toErrorMessage(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (detail.message) return detail.message;
  try { return JSON.stringify(detail); } catch { return fallback; }
}

const WINDOWS = [
  { key: "today", labelKey: "win_today" },
  { key: "yesterday", labelKey: "win_yesterday" },
  { key: "3d", labelKey: "win_3d" },
  { key: "week", labelKey: "win_week" },
  { key: "month", labelKey: "win_month" },
];

function Kpi({ label, value, sub, tint, accent, fg }) {
  const cardStyle = tint
    ? { background: tint, borderColor: tint, borderLeft: `4px solid ${accent}` }
    : undefined;
  return (
    <div className="kpi" style={cardStyle}>
      <div className="label" style={fg ? { color: fg, opacity: 0.85 } : undefined}>{label}</div>
      <div className="value" style={fg ? { color: fg } : undefined}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: "0.8rem", ...(fg ? { color: fg, opacity: 0.7 } : {}) }}>{sub}</div>}
    </div>
  );
}

const DAY_COLORS = {
  1: "#3a6db8",
  2: "#008080",
  3: "#5a8c3d",
  4: "#d4af37",
  5: "#c47a2c",
  6: "#a85a4f",
  7: "#7d52a3",
};

function DayDonut({ data, window, t }) {
  let view;
  if (window === "today") {
    const todayDow = ((new Date().getUTCDay() + 6) % 7) + 1; // 1=Mon..7=Sun
    view = data.filter((d) => d.day === todayDow);
  } else {
    view = data;
  }
  const total = view.reduce((s, d) => s + (d.orders || 0), 0);
  if (total === 0) {
    return (
      <div className="muted" style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: 200, fontSize: "0.9rem",
      }}>
        {t("no_data_window")}
      </div>
    );
  }

  const SIZE = 200;
  const CENTER = SIZE / 2;
  const RADIUS = 70;
  const STROKE = 28;
  const CIRC = 2 * Math.PI * RADIUS;

  let peakIdx = 0;
  for (let i = 1; i < view.length; i++) {
    if ((view[i].orders || 0) > (view[peakIdx].orders || 0)) peakIdx = i;
  }

  const colorFor = (d) => DAY_COLORS[d.day] || "var(--brand)";

  let cumPct = 0;
  const slices = view.map((d, i) => {
    const pct = (d.orders || 0) / total;
    const slice = {
      i, day: d.name, orders: d.orders || 0, pct,
      offset: -CIRC * cumPct,
      dashLen: CIRC * pct,
    };
    cumPct += pct;
    return slice;
  });

  const peak = view[peakIdx];
  const peakPct = Math.round((peak.orders / total) * 100);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 20,
      flexWrap: "wrap", justifyContent: "center",
    }}>
      <svg
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        style={{ width: 200, height: 200, maxWidth: "100%", flexShrink: 0 }}
        role="img"
        aria-label={`Orders by day of week. Peak day: ${peak.name}, ${peak.orders} orders (${peakPct}%).`}
      >
        <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
          {slices.map((s) => (
            <circle
              key={s.i}
              cx={CENTER} cy={CENTER} r={RADIUS}
              fill="none"
              stroke={colorFor(view[s.i])}
              strokeWidth={STROKE}
              strokeDasharray={`${s.dashLen} ${CIRC - s.dashLen}`}
              strokeDashoffset={s.offset}
            >
              <title>{`${s.day}: ${s.orders} orders (${Math.round(s.pct * 100)}%)`}</title>
            </circle>
          ))}
        </g>
        <text
          x={CENTER} y={CENTER - 4} textAnchor="middle"
          style={{ fontSize: 30, fontWeight: 700, fill: "var(--odra-navy)" }}
        >
          {peak.orders}
        </text>
        <text
          x={CENTER} y={CENTER + 16} textAnchor="middle"
          style={{ fontSize: 11, fill: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          {peak.name} · {peakPct}%
        </text>
      </svg>
      <ul className="day-legend">
        {view.map((d, i) => (
          <li key={d.day} className={"day-legend-row" + (i === peakIdx ? " peak" : "")}>
            <span className="day-legend-swatch" style={{ background: colorFor(d) }} />
            <span className="day-legend-label">{d.name}</span>
            <span className="day-legend-val">{d.orders}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bars({ rows, valueKey, labelKey, fmt, t }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div>
      {rows.length === 0 && <p className="muted">{t("no_data")}</p>}
      {rows.map((r, i) => (
        <div key={i} className="bar-row">
          <div className="bar-name" title={r[labelKey]}>{r[labelKey]}</div>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }}
            />
          </div>
          <div className="bar-val">{fmt ? fmt(r) : r[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

function ratingStyle(v) {
  if (v == null) return { bg: "transparent", fg: "#888" };
  if (v >= 4) return { bg: "#EAF3DE", fg: "#27500A" };
  if (v >= 3) return { bg: "#FAEEDA", fg: "#854F0B" };
  return { bg: "#FCEBEB", fg: "#A32D2D" };
}

export default function ReportsPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const currency = getCurrency();
  const [win, setWin] = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [summary, setSummary] = useState(null);
  const [serviceSpeed, setServiceSpeed] = useState(null);
  const [stationSpeed, setStationSpeed] = useState(null);
  const stationsOn = hasFeature("stations");
  // Advanced analytics (cook/prep time + service & delivery speed) — Hotel+.
  const advancedOn = hasFeature("advanced_analytics");
  // Group & event order analytics — gated by the group_orders feature.
  const eventsOn = hasFeature("group_orders");
  const [groupEvents, setGroupEvents] = useState(null);
  const [revenueChannels, setRevenueChannels] = useState(null);
  const [cancellations, setCancellations] = useState(null);
  const [top, setTop] = useState([]);
  const [low, setLow] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [byTable, setByTable] = useState([]);
  const [byStaff, setByStaff] = useState([]);
  const [recipePlan, setRecipePlan] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [orderMix, setOrderMix] = useState(null);
  const [openAreas, setOpenAreas] = useState({});
  const toggleArea = (a) => setOpenAreas((pr) => ({ ...pr, [a]: !pr[a] }));
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const showFd = hasFeature("front_desk");
  const [fdSnap, setFdSnap] = useState(null);

  const customActive = Boolean(customStart && customEnd);
  const range = customActive ? { start: customStart, end: customEnd } : { window: win };
  const wideRange = customActive ? range : { window: win === "today" ? "week" : win };
  const rangeKey = JSON.stringify(range);

  useEffect(() => {
    if (!showFd) return;
    const iso = (dt) => dt.toISOString().slice(0, 10);
    const today = new Date();
    const to = customActive ? customEnd : iso(today);
    let from;
    if (customActive) from = customStart;
    else if (win === "today") from = iso(today);
    else if (win === "week") { const s = new Date(today); s.setDate(today.getDate() - 6); from = iso(s); }
    else if (win === "month") { const s = new Date(today); s.setDate(1); from = iso(s); }
    else { const s = new Date(today); s.setDate(today.getDate() - 30); from = iso(s); }
    fetchFrontDeskSnapshot(from, to).then(setFdSnap).catch(() => {});
    // eslint-disable-next-line
  }, [rangeKey, showFd]);

  const pickPreset = (key) => {
    setCustomStart("");
    setCustomEnd("");
    setWin(key);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    const recipeCall = hasFeature("recipe_plan")
      ? fetchRecipePlan(wideRange, 8)
      : Promise.resolve(null);
    const lowCall = hasFeature("menu_engineering")
      ? fetchLowPerformers(wideRange)
      : Promise.resolve([]);
    const speedCall = advancedOn ? fetchServiceSpeed(range) : Promise.resolve(null);
    Promise.all([
      fetchSummary(range),
      fetchTopItems(range, 10),
      fetchHourly(range),
      fetchDaily(wideRange),
      fetchByTable(range),
      fetchByStaff(range),
      lowCall,
      recipeCall,
      fetchPatterns(range),
      fetchOrderMix(range),
      speedCall,
    ])
      .then(([s, tt, h, d, bt, bs, lp, rp, pat, mix, svc]) => {
        setSummary(s);
        setTop(tt);
        setHourly(h);
        setDaily(d);
        setByTable(bt);
        setByStaff(bs);
        setLow(lp);
        setRecipePlan(rp);
        setPatterns(pat);
        setOrderMix(mix);
        setServiceSpeed(svc);
      })
      .catch((e) => setError(toErrorMessage(e, t("failed_load"))))
      .finally(() => setLoading(false));
  }, [rangeKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Station prep-speed is part of advanced analytics (Hotel+) and needs
    // station routing on. Skip the call otherwise (it would 402).
    if (!advancedOn || !stationsOn) return;
    let alive = true;
    fetchStationSpeed(range).then((d) => { if (alive) setStationSpeed(d); }).catch(() => {});
    return () => { alive = false; };
  }, [rangeKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!eventsOn) return;
    let alive = true;
    fetchGroupEvents(range).then((d) => { if (alive) setGroupEvents(d); }).catch(() => {});
    return () => { alive = false; };
  }, [rangeKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    fetchRevenueChannels(range).then((d) => { if (alive) setRevenueChannels(d); }).catch(() => {});
    return () => { alive = false; };
  }, [rangeKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    fetchCancellations(range).then((d) => { if (alive) setCancellations(d); }).catch(() => {});
    return () => { alive = false; };
  }, [rangeKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const peakHour = useMemo(() => {
    if (!hourly.length) return null;
    return hourly.reduce((a, b) => (b.orders > a.orders ? b : a));
  }, [hourly]);

  const maxHourly = useMemo(
    () => Math.max(1, ...hourly.map((h) => h.orders)),
    [hourly]
  );
  const dpLabelR = { breakfast: t("dp_breakfast"), lunch: t("dp_lunch"), dinner: t("dp_dinner"), late: t("dp_late") };
  const daypartRows = ((patterns && patterns.daypart) || []).map((d) => ({ label: dpLabelR[d.key] || d.key, value: d.revenue }));
  const dowRows = ((patterns && patterns.dow) || []).map((d) => ({ label: t("bd" + d.day), value: d.revenue }));
  const tableAreas = useMemo(() => {
    const has = byTable.some((r) => r.area && String(r.area).trim());
    if (!has) return null;
    const groups = {};
    byTable.forEach((r) => {
      const a = (r.area && String(r.area).trim()) || t("area_unassigned");
      if (!groups[a]) groups[a] = { area: a, orders: 0, revenue: 0, tables: [] };
      groups[a].orders += r.orders; groups[a].revenue += r.revenue; groups[a].tables.push(r);
    });
    return Object.values(groups).sort((x, y) => y.revenue - x.revenue);
  }, [byTable]);  // eslint-disable-line react-hooks/exhaustive-deps
  const _ch = (orderMix && orderMix.channels) || [];
  const chSelf = _ch.find((c) => c.key === "self") || { orders: 0, revenue: 0, aov: 0 };
  const chWaiter = _ch.find((c) => c.key === "waiter") || { orders: 0, revenue: 0, aov: 0 };
  const chTotal = chSelf.orders + chWaiter.orders;
  const chSelfPct = chTotal ? Math.round((chSelf.orders / chTotal) * 100) : 0;
  const rounds = orderMix && orderMix.rounds;
  const miniBars = (rows, color, fmt) => {
    const max = Math.max(1, ...rows.map((r) => r.value || 0));
    return rows.map((r, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, margin: "3px 0" }}>
        <div style={{ width: 52, fontSize: 12, color: "#5f6b78" }}>{r.label}</div>
        <div style={{ flex: 1, height: 9, background: "#eef2f6", borderRadius: 5, overflow: "hidden" }}>
          <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: color }} />
        </div>
        <div style={{ width: 72, textAlign: "right", fontSize: 12 }}>{fmt ? fmt(r.value) : r.value}</div>
      </div>
    ));
  };


  return (
    <>
      <div className="space-between" style={{ marginBottom: 16 }}>
        <h1>{t("page_title")}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div className="tabs" style={{ margin: 0 }}>
            {WINDOWS.map((w) => (
              <div
                key={w.key}
                className={"tab " + (!customActive && win === w.key ? "active" : "")}
                onClick={() => pickPreset(w.key)}
              >
                {t(w.labelKey)}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem" }}>
            <span className="muted">{t("custom_from")}</span>
            <input type="date" value={customStart} max={customEnd || undefined}
                   onChange={(e) => setCustomStart(e.target.value)} />
            <span className="muted">{t("custom_to")}</span>
            <input type="date" value={customEnd} min={customStart || undefined}
                   onChange={(e) => setCustomEnd(e.target.value)} />
            {customActive && (
              <button type="button" className="btn secondary small"
                      onClick={() => { setCustomStart(""); setCustomEnd(""); }}>
                {t("custom_clear")}
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {loading && <p className="muted">{t("loading")}</p>}

      {summary && (
        <section className="kpi-grid" style={{ marginBottom: 24 }}>
          <Kpi label={t("kpi_revenue")} value={formatPrice(summary.revenue, currency)} sub={t("kpi_items").replace("{n}", summary.total_items_sold)} tint="#EAF3DE" accent="#639922" fg="#27500A" />
          <Kpi label={t("kpi_orders")} value={summary.total_orders} sub={t("kpi_active").replace("{n}", summary.active_orders)} tint="#E6F1FB" accent="#378ADD" fg="#0C447C" />
          <Kpi label={t("kpi_completed")} value={summary.completed_orders} tint="#E1F5EE" accent="#1D9E75" fg="#085041" />
          <Kpi label={t("kpi_cancelled")} value={summary.cancelled_orders} tint="#FBEAEA" accent="#D9534F" fg="#8A2B27" />
          <Kpi label={t("kpi_avg_order")} value={formatPrice(summary.average_order_value, currency)} tint="#EEEDFE" accent="#7F77DD" fg="#2E2A6B" />
        </section>
      )}

      {showFd && fdSnap && (
        <section style={{ background: "#fff", border: "1px solid #eef2f2", borderRadius: 12, padding: 16, marginBottom: 24 }}>
          <FrontDeskFacts snapshot={fdSnap} cur={currency || "ETB"} scope="range" />
        </section>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flexBasis: "100%", order: 0, background: "#E1F5EE", borderLeft: "4px solid #1D9E75", borderRadius: 8, padding: "8px 14px", marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#085041" }}>{t("grp_sales")}</h2></div>
        <div style={{ flexBasis: "100%", order: 10, background: "#FAEEDA", borderLeft: "4px solid #BA7517", borderRadius: 8, padding: "8px 14px", marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#633806" }}>{t("grp_busy")}</h2></div>
        {stationsOn && stationSpeed && Object.keys(stationSpeed.stations || {}).length > 0 && (
        <section className="card" style={{ order: 15, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>🍳 Prep time by station</h3>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {["kitchen", "bar", "cafe"].filter((k) => stationSpeed.stations[k]).map((k) => {
              const b = stationSpeed.stations[k];
              const label = { kitchen: "🍳 Kitchen", bar: "🍺 Bar", cafe: "☕ Café" }[k];
              return (
                <div key={k} style={{ flex: "1 1 110px", background: "#F1F5F9", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: "#085041", lineHeight: 1.15 }}>
                    {b.median_min == null ? "\u2014" : <>{b.median_min} <span style={{ fontSize: 13 }}>{t("svc_min")}</span></>}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>{b.n} items</div>
                </div>
              );
            })}
          </div>
        </section>
        )}
        {serviceSpeed && (
        <section className="card" style={{ order: 14, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("svc_title")}</h3>
          {(serviceSpeed.cook.n === 0 && serviceSpeed.total_wait.n === 0) ? (
            <p className="muted">{t("svc_none")}</p>
          ) : (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {[["cook", t("svc_cook"), "#FAEEDA", "#BA7517", "#633806"], ["total_wait", t("svc_wait"), "#E1F5EE", "#1D9E75", "#085041"]].map(([k, label, tint, accent, fg]) => {
                const b = serviceSpeed[k];
                return (
                  <div key={k} style={{ flex: "1 1 150px", background: tint, borderRadius: 12, padding: "12px 14px" }}>
                    <div style={{ fontSize: 13, color: fg, fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1.15 }}>
                      {b.median_min == null ? "\u2014" : <>{b.median_min} <span style={{ fontSize: 14, fontWeight: 600 }}>{t("svc_min")}</span></>}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {b.median_min == null ? "\u2014" : `${t("svc_median")} \u00b7 ${t("svc_avg")} ${b.avg_min} \u00b7 ${b.n} ${t("svc_orders")}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        )}
        {revenueChannels && revenueChannels.total_revenue > 0 && (
        <section className="card" style={{ order: 7, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("ch_title")}</h3>
          <div style={{ display: "grid", gap: 8 }}>
            {revenueChannels.channels.filter((c) => c.revenue > 0 || c.orders > 0).map((c) => {
              const labels = { qr: t("ch_qr"), captain: t("ch_captain"), room: t("ch_room"), group: t("ch_group") };
              const pct = revenueChannels.total_revenue ? Math.round((c.revenue / revenueChannels.total_revenue) * 100) : 0;
              return (
                <div key={c.key}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.88rem" }}>
                    <span>{labels[c.key] || c.key} <span className="muted" style={{ fontSize: 12 }}>· {t("ch_orders_n").replace("{n}", c.orders)}</span></span>
                    <b>{formatPrice(c.revenue, currency)} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>{pct}%</span></b>
                  </div>
                  <div style={{ height: 8, background: "#eef1f2", borderRadius: 4, marginTop: 3, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#008080" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        )}
        {cancellations && (
        <section className="card" style={{ order: 8, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("cx_title")}</h3>
          {cancellations.cancelled === 0 ? (
            <p className="muted">{t("cx_none")}</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  [t("cx_cancelled"), cancellations.cancelled],
                  [t("cx_rate"), `${cancellations.cancellation_rate}%`],
                  [t("cx_lost"), formatPrice(cancellations.lost_revenue, currency)],
                ].map(([label, value], i) => (
                  <div key={i} style={{ flex: "1 1 110px", background: "#FBEAF0", borderRadius: 12, padding: "10px 12px" }}>
                    <div style={{ fontSize: 12, color: "#72243E", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#72243E", lineHeight: 1.15 }}>{value}</div>
                  </div>
                ))}
              </div>
              {cancellations.top_items && cancellations.top_items.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>{t("cx_top")}</div>
                  {cancellations.top_items.map((it, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", padding: "2px 0" }}>
                      <span>{it.name}</span>
                      <span className="muted">{t("cx_qty").replace("{n}", it.qty)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
        )}
        {eventsOn && groupEvents && (
        <section className="card" style={{ order: 16, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("ev_title")}</h3>
          {groupEvents.events === 0 && groupEvents.cancelled === 0 ? (
            <p className="muted">{t("ev_none")}</p>
          ) : (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  [t("ev_events"), groupEvents.events, groupEvents.cancelled ? t("ev_cancelled").replace("{n}", groupEvents.cancelled) : null],
                  [t("ev_guests"), groupEvents.guests, null],
                  [t("ev_revenue"), formatPrice(groupEvents.revenue, currency), null],
                  [t("ev_per_guest"), formatPrice(groupEvents.avg_per_guest, currency), null],
                  [t("ev_upcoming"), groupEvents.upcoming, null],
                ].map(([label, value, sub], i) => (
                  <div key={i} style={{ flex: "1 1 110px", background: "#FBF6E6", borderRadius: 12, padding: "10px 12px" }}>
                    <div style={{ fontSize: 12, color: "#7e671f", fontWeight: 600 }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#063B48", lineHeight: 1.15 }}>{value}</div>
                    {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
                  </div>
                ))}
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                {t("ev_buffet")} {formatPrice(groupEvents.buffet_revenue, currency)} &nbsp;·&nbsp; {t("ev_alacarte")} {formatPrice(groupEvents.alacarte_revenue, currency)}
              </div>
              {groupEvents.top_events && groupEvents.top_events.length > 0 && (
                <table style={{ width: "100%", marginTop: 10, fontSize: "0.85rem", borderCollapse: "collapse" }}>
                  <thead><tr style={{ textAlign: "left", color: "#5f6b78" }}>
                    <th style={{ padding: "4px 6px" }}>{t("ev_top")}</th>
                    <th style={{ padding: "4px 6px", textAlign: "right" }}>{t("ev_guests")}</th>
                    <th style={{ padding: "4px 6px", textAlign: "right" }}>{t("ev_revenue")}</th>
                  </tr></thead>
                  <tbody>
                    {groupEvents.top_events.map((e) => (
                      <tr key={e.id} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "4px 6px" }}>{e.name}{e.organizer ? <span className="muted"> · {e.organizer}</span> : null}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>{e.headcount} {t("ev_pax")}{e.days > 1 ? ` ×${e.days}` : ""}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600 }}>{formatPrice(e.revenue, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
        )}
        <div style={{ flexBasis: "100%", order: 20, background: "#E6F1FB", borderLeft: "4px solid #378ADD", borderRadius: 8, padding: "8px 14px", marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#0C447C" }}>{t("grp_team")}</h2></div>
        {hasFeature("recipe_plan") && <div style={{ flexBasis: "100%", order: 30, background: "#EEEDFE", borderLeft: "4px solid #7F77DD", borderRadius: 8, padding: "8px 14px", marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#2E2A6B" }}>{t("grp_kitchen")}</h2></div>}
        <div style={{ flexBasis: "100%", order: 5, background: "#FBEAF0", borderLeft: "4px solid #D4537E", borderRadius: 8, padding: "8px 14px", marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#72243E" }}>{t("grp_ordering")}</h2></div>
        <section className="card" style={{ order: 6, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("how_order")}</h3>
          {chTotal === 0 ? <p className="muted">{t("no_data")}</p> : (
            <>
              <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ width: `${chSelfPct}%`, background: "#008080" }} />
                <div style={{ width: `${100 - chSelfPct}%`, background: "#BA7517" }} />
              </div>
              {[["ch_self", "#008080", chSelf], ["ch_waiter", "#BA7517", chWaiter]].map(([k, c, o]) => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: c, display: "inline-block" }} />
                  <span style={{ flex: 1 }}>{t(k)}</span>
                  <span className="muted" style={{ fontSize: 13 }}>{o.orders} · {chTotal ? Math.round((o.orders / chTotal) * 100) : 0}% · {t("aov_short")} {formatPrice(o.aov, currency)}</span>
                </div>
              ))}
              {chSelf.orders > 0 && chWaiter.orders > 0 && (
                <div style={{ fontSize: 12, marginTop: 8, fontWeight: 600, color: "#1d7a4d" }}>
                  {chSelf.aov === chWaiter.aov ? t("aov_even") : t("aov_lead").replace("{w}", chSelf.aov > chWaiter.aov ? t("ch_self") : t("ch_waiter"))}
                </div>
              )}
            </>
          )}
        </section>
        <section className="card" style={{ order: 7, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("rounds_title")}</h3>
          {!rounds || rounds.tabs === 0 ? <p className="muted">{t("no_rounds")}</p> : (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 28, fontWeight: 700, color: "#063b48" }}>{rounds.avg_rounds}</span>{" "}
                <span className="muted">{t("rounds_per_tab")} · {rounds.tabs} {t("tabs_word")}</span>
              </div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>{t("round_dist")}</div>
              {miniBars(rounds.distribution.map((d) => ({ label: d.rounds, value: d.tabs })), "#378ADD", null)}
              <div className="muted" style={{ fontSize: 13, margin: "8px 0 4px" }}>{t("rev_by_round")}</div>
              {miniBars(rounds.revenue_by_round.map((d) => ({ label: t("round_n").replace("{n}", d.round), value: d.revenue })), "#1D9E75", (v) => formatPrice(v, currency))}
            </>
          )}
        </section>
        <section className="card" style={{ order: 1, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("most_ordered")}</h3>
          <p className="muted" style={{ marginTop: -8 }}>
            {t("most_ordered_sub")}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ width: 52, color: "#fff" }}></th>
                  <th style={{ textAlign: "left", padding: "8px 10px", color: "#fff" }}>{t("th_item")}</th>
                  <th style={{ textAlign: "right", padding: "8px 10px", color: "#fff" }}>{t("th_sold")}</th>
                  <th style={{ textAlign: "right", padding: "8px 10px", color: "#fff" }}>{t("th_revenue")}</th>
                  <th style={{ textAlign: "right", padding: "8px 10px", color: "#fff" }}>{t("th_rating")}</th>
                </tr>
              </thead>
              <tbody>
                {top.length === 0 && (<tr><td colSpan={5} className="muted" style={{ padding: 10 }}>{t("no_data")}</td></tr>)}
                {top.map((it, i) => {
                  const rb = ratingStyle(it.avg_food_rating);
                  return (
                    <tr key={it.menu_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                      <td style={{ padding: "6px 8px" }}>
                        {it.image_url
                          ? <img src={resolveImageUrl(it.image_url)} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
                          : <div style={{ width: 40, height: 40, borderRadius: 8, background: "#eef2f6" }} />}
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        <b>{it.name}</b>
                        {it.category && <div className="muted" style={{ fontSize: "0.8rem" }}>{it.category}</div>}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600 }}>{it.quantity_sold}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{formatPrice(it.revenue, currency)}</td>
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>
                        {it.avg_food_rating == null
                          ? <span className="muted">—</span>
                          : <span style={{ background: rb.bg, color: rb.fg, padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{it.avg_food_rating} ★</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card" style={{ order: 12, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("orders_by_hour")}</h3>
          {peakHour && peakHour.orders > 0 && (
            <p className="muted" style={{ marginTop: -8 }}>
              {t("peak_hour")} <b>{String(peakHour.hour).padStart(2, "0")}:00</b> {t("peak_hour_orders").replace("{n}", peakHour.orders)}
            </p>
          )}
          <div className="hour-chart">
            {hourly.map((h) => (
              <div
                key={h.hour}
                className="bar"
                title={`${String(h.hour).padStart(2, "0")}:00 — ${h.orders} orders`}
                style={{ height: `${(h.orders / maxHourly) * 100}%` }}
              />
            ))}
          </div>
          <div className="hour-axis">
            {hourly.map((h) => (
              <div key={h.hour}>{h.hour % 3 === 0 ? h.hour : ""}</div>
            ))}
          </div>
        </section>

        <section className="card" style={{ order: 13, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("orders_by_day")}</h3>
          <p className="muted" style={{ marginTop: -8 }}>
            {customActive
              ? t("day_sub_custom")
              : win === "today"
              ? t("day_sub_today")
              : win === "month"
              ? t("day_sub_month")
              : t("day_sub_week")}
          </p>
          <DayDonut data={daily} window={customActive ? "custom" : win} t={t} />
        </section>

        <section className="card" style={{ order: 11, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("busy_title")}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 18 }}>
            {[["by_daypart", daypartRows, "#1D9E75"], ["by_dow", dowRows, "#7F77DD"]].map(([key, rows, color]) => {
              const max = Math.max(1, ...rows.map((r) => r.value || 0));
              return (
                <div key={key}>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>{t(key)}</div>
                  {rows.length === 0 ? <p className="muted">{t("no_data")}</p> : rows.map((r, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
                      <div style={{ width: 64, fontSize: 13, color: "#5f6b78", whiteSpace: "nowrap" }}>{r.label}</div>
                      <div style={{ flex: 1, height: 10, background: "#eef2f6", borderRadius: 6, overflow: "hidden" }}>
                        <div style={{ width: `${(r.value / max) * 100}%`, height: "100%", background: color }} />
                      </div>
                      <div style={{ width: 84, textAlign: "right", fontSize: 12 }}>{formatPrice(r.value, currency)}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </section>

        <section className="card" style={{ order: 3, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("revenue_by_table")}</h3>
          {byTable.length === 0 ? (
            <p className="muted">{t("no_table_orders")}</p>
          ) : tableAreas ? (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_area")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_orders")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {tableAreas.map((g, gi) => (
                  <React.Fragment key={g.area}>
                    <tr style={{ background: gi % 2 ? "#f2f9f8" : "#fff", cursor: "pointer" }} onClick={() => toggleArea(g.area)}>
                      <td style={{ padding: "6px 10px", fontWeight: 600 }}>
                        <span style={{ display: "inline-block", width: 14, color: "#008080" }}>{openAreas[g.area] ? "\u25be" : "\u25b8"}</span>
                        {g.area} <span className="muted" style={{ fontWeight: 400 }}>({g.tables.length})</span>
                      </td>
                      <td style={{ padding: "6px 10px", fontWeight: 600 }}>{g.orders}</td>
                      <td style={{ padding: "6px 10px", fontWeight: 600 }}>{formatPrice(g.revenue, currency)}</td>
                    </tr>
                    {openAreas[g.area] && g.tables.map((tbl) => (
                      <tr key={tbl.table_id} style={{ background: "#fbfcfd" }}>
                        <td style={{ padding: "4px 10px 4px 32px", color: "#5f6b78" }}>#{tbl.table_number}</td>
                        <td style={{ padding: "4px 10px", color: "#5f6b78" }}>{tbl.orders}</td>
                        <td style={{ padding: "4px 10px", color: "#5f6b78" }}>{formatPrice(tbl.revenue, currency)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_table")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_orders")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_revenue")}</th>
                </tr>
              </thead>
              <tbody>
                {byTable.map((r, i) => (
                  <tr key={r.table_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                    <td>#{r.table_number}</td>
                    <td>{r.orders}</td>
                    <td>{formatPrice(r.revenue, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {hasFeature("menu_engineering") && (
        <section className="card" style={{ order: 2, flex: "1 1 380px" }}>
          <h3 style={{ marginTop: 0 }}>{t("low_performers")}</h3>
          <p className="muted" style={{ marginTop: -8 }}>
            {t("low_performers_sub")}
          </p>
          {low.length === 0 ? (
            <p className="muted">{t("not_enough_data")}</p>
          ) : (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_item")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_category")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_sold")}</th>
                </tr>
              </thead>
              <tbody>
                {low.map((l, i) => (
                  <tr key={l.menu_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                    <td>{l.name}</td>
                    <td className="muted">{l.category || "—"}</td>
                    <td>{l.quantity_sold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        )}
        <section className="card" style={{ flexBasis: "100%", order: 21 }}>
          <h3 style={{ marginTop: 0 }}>{t("by_waiter")}</h3>
          <p className="muted" style={{ marginTop: -8 }}>
            {t("by_waiter_sub")}
          </p>
          {byStaff.length === 0 ? (
            <p className="muted">{t("no_waiter_data")}</p>
          ) : (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_waiter")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_role")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_orders")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_items")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_avg_check")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_revenue")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_cancelled")}</th>
                </tr>
              </thead>
              <tbody>
                {byStaff.map((s, i) => (
                  <tr key={s.staff_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {s.photo_url ? (
                          <img src={resolveImageUrl(s.photo_url)} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover" }} />
                        ) : (
                          <span style={{ width: 30, height: 30, borderRadius: "50%", background: "#e6eaee", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#6b7484", fontWeight: 700, fontSize: "0.8rem" }}>
                            {(s.name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                        )}
                        <b>{s.name}</b>
                      </span>
                    </td>
                    <td className="muted" style={{ textTransform: "capitalize" }}>{s.role}</td>
                    <td>{s.orders}</td>
                    <td>{s.items_sold}</td>
                    <td>{formatPrice(s.average_check, currency)}</td>
                    <td>{formatPrice(s.revenue, currency)}</td>
                    <td style={{ color: s.cancelled_orders > 0 ? "var(--danger, #b13c38)" : undefined, fontWeight: s.cancelled_orders > 0 ? 700 : 400 }}>
                      {s.cancelled_orders}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {hasFeature("recipe_plan") && (
        <section className="card" style={{ flexBasis: "100%", order: 31 }}>
          <h3 style={{ marginTop: 0 }}>{t("recipe_plan")}</h3>
          <p className="muted" style={{ marginTop: -8 }}>
            {t("recipe_plan_sub").replace("{n}", recipePlan?.window || t("recipe_plan_window"))}
          </p>
          {!recipePlan || recipePlan.items.length === 0 ? (
            <p className="muted">{t("not_enough_orders")}</p>
          ) : (
            <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#008080" }}>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}></th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_item")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_sold")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_per_day")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_forecast")}</th>
                  <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_recipe")}</th>
                </tr>
              </thead>
              <tbody>
                {recipePlan.items.map((it, i) => (
                  <tr key={it.menu_id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                    <td style={{ width: 60 }}>
                      {it.image_url && (
                        <img
                          src={resolveImageUrl(it.image_url)}
                          alt=""
                          style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                        />
                      )}
                    </td>
                    <td>
                      <b>{it.name}</b>
                      {it.name_am && (
                        <span lang="am" className="muted" style={{ marginLeft: 6 }}>
                          ({it.name_am})
                        </span>
                      )}
                      <div className="muted" style={{ fontSize: "0.8rem" }}>{it.category}</div>
                    </td>
                    <td>{it.quantity_sold}</td>
                    <td>{it.per_day}</td>
                    <td><b>{it.forecast_next_window}</b></td>
                    <td style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", color: "var(--muted)" }}>
                      {it.recipe || <span className="muted">{t("no_recipe")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        )}
      </div>
    </>
  );
}
